import React, { memo, useState } from "react";
import { Dropdown, Tooltip, Select } from "antd";
import { PushpinOutlined, EditOutlined, DeleteOutlined, MoreOutlined } from "@ant-design/icons";
import { conversationTags } from "./conversationLibrary.mjs";

export const ConversationFilters = ({ topics, topic, pinned, onTopicChange, onPinnedChange, resultCount, totalCount, onClear }) => (
  <div className="facility-ai-library-filters">
    <div className="facility-ai-library-tabs" aria-label="Conversation view">
      <button type="button" aria-pressed={!pinned} onClick={() => onPinnedChange(false)}>All conversations</button>
      <button type="button" aria-pressed={pinned} onClick={() => onPinnedChange(true)}><PushpinOutlined /> Pinned</button>
    </div>
    <Select aria-label="Filter conversations by topic" placeholder="All topics" value={topic || undefined} allowClear onChange={(value) => onTopicChange(value || "")} options={topics.map(([label, count]) => ({ value: label, label: `${label} · ${count}` }))} />
    <div className="facility-ai-library-result" aria-live="polite">
      <span>{resultCount} of {totalCount} conversations</span>
      {onClear && <button type="button" onClick={onClear}>Clear filters</button>}
    </div>
  </div>
);

/* Sidebar row dropdown items (3.10): shared by ConversationRow below and
   buildConversationMenu (mobile header). */
export const conversationMenuItems = (isPinned) => [
  {
    key: "pin",
    icon: <PushpinOutlined />,
    label: isPinned ? "Unpin" : "Pin to top",
  },
  {
    key: "rename",
    icon: <EditOutlined />,
    label: "Rename",
  },
  {
    key: "delete",
    icon: <DeleteOutlined />,
    danger: true,
    label: "Delete",
  },
];

/* One sidebar conversation row (3.10), memoized so stream frames and
   transcript updates no longer re-render every row (Dropdown + Tooltip
   included). The Dropdown menu is built here from the module-level config
   above; every handler prop is a stable useCallback, so a row only
   re-renders when its conversation object, active state, or disabled flag
   actually changes. */
export const ConversationRow = memo(function ConversationRow({
  conversation,
  isActive,
  disabled,
  onSelect,
  onMenuClick,
  onTagClick,
  activeTopic,
  dateLabel,
}) {
  const [pinPending, setPinPending] = useState(false);
  const handleMenuClick = ({ key }) => onMenuClick(conversation, key);
  return (
    <div className={`facility-ai-conversation-row${isActive ? " is-active" : ""}`}>
      <div className="facility-ai-conversation-body">
      <button
        type="button"
        title={conversation.title || "Untitled conversation"}
        aria-current={isActive ? "true" : undefined}
        className="facility-ai-conversation-select"
        onClick={() => onSelect(conversation.id)}
        disabled={disabled}
      >
        <span className="facility-ai-conversation-title">
          <span className="facility-ai-conversation-title-text">
            {conversation.title || "Untitled conversation"}
          </span>
        </span>
        <span className="facility-ai-conversation-date">
          {dateLabel}
        </span>
      </button>
      <div className="facility-ai-conversation-tags">
        {conversationTags(conversation).map((tag) => <button type="button" key={tag} aria-pressed={activeTopic === tag} title={`Filter by ${tag} · auto-tagged from title`} onClick={() => onTagClick(tag)}>{tag}</button>)}
      </div>
      </div>
      <Tooltip title={conversation.is_pinned ? "Unpin conversation" : "Pin to top"}>
        <button type="button" className="facility-ai-pin-action" aria-label={`${conversation.is_pinned ? "Unpin" : "Pin to top"}: ${conversation.title || "conversation"}`} aria-pressed={Boolean(conversation.is_pinned)} disabled={disabled || pinPending} onClick={async () => {
          setPinPending(true);
          try { await onMenuClick(conversation, "pin"); } finally { setPinPending(false); }
        }}><PushpinOutlined /></button>
      </Tooltip>
      <Dropdown
        menu={{ items: conversationMenuItems(conversation.is_pinned), onClick: handleMenuClick }}
        trigger={["click"]}
        placement="bottomRight"
      >
        <span className={`facility-ai-conversation-menu-wrap${disabled ? " is-disabled" : ""}`}>
          <Tooltip title="More actions">
            <button
              type="button"
              className="facility-ai-conversation-menu"
              aria-label={`Actions for ${conversation.title || "conversation"}`}
              disabled={disabled}
            >
              <MoreOutlined />
            </button>
          </Tooltip>
        </span>
      </Dropdown>
    </div>
  );
});

